// ABOUTME: Reverse-lookup panel for the Pricing Trace drawer: find which rules can emit a price key.
// ABOUTME: Shows exact decision-table (tableEmit) matches and ranked emit-template candidates.

import { Box, Button, Chip, CircularProgress, Stack, TextField, Typography } from '@ds/coolshop';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { KeyEmitterMatch, useKeyEmitters } from './useKeyEmitters';

function MatchRow({ match }: { match: KeyEmitterMatch }) {
    const { formatMessage } = useIntl();

    return (
        <Stack direction="row" alignItems="center" gap={1} paddingY={0.5} flexWrap="wrap">
            <Typography variant="body2">
                <code>{`${match.procedureCode} · #${match.ruleIndex} ${match.ruleType}`}</code>
            </Typography>
            {match.bucket && <Chip size="small" label={match.bucket} />}
            {match.tableRef && (
                <Typography variant="caption" color="textSecondary">
                    {`${formatMessage({
                        id: 'cpq.key-emitters.table',
                        defaultMessage: 'table',
                    })}: `}
                    <code>{match.tableRef}</code>
                </Typography>
            )}
            {match.keyExpression && (
                <Typography variant="caption" color="textSecondary">
                    <code>{match.keyExpression}</code>
                </Typography>
            )}
            {match.models.length > 0 && (
                <Typography variant="caption" color="textSecondary">
                    {`${formatMessage({
                        id: 'cpq.key-emitters.models',
                        defaultMessage: 'models',
                    })}: ${match.models.join(', ')}`}
                </Typography>
            )}
        </Stack>
    );
}

export function KeyEmitterSearch() {
    const { formatMessage } = useIntl();
    const { search, loading, error, result } = useKeyEmitters();
    const [key, setKey] = useState('');

    const submit = () => {
        const trimmed = key.trim();
        if (trimmed) search(trimmed);
    };

    return (
        <Stack gap={1}>
            <Typography variant="subtitle2">
                {formatMessage({
                    id: 'cpq.key-emitters.title',
                    defaultMessage: 'Find emitting rules',
                })}
            </Typography>
            <Stack direction="row" gap={1} alignItems="center">
                <Box flex={1}>
                    <TextField
                        label={formatMessage({
                            id: 'cpq.key-emitters.key-label',
                            defaultMessage: 'Price key',
                        })}
                        value={key}
                        onChange={e => setKey(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') submit();
                        }}
                        fullWidth
                    />
                </Box>
                <Button onClick={submit} disabled={loading || key.trim() === ''}>
                    {formatMessage({ id: 'cpq.key-emitters.search', defaultMessage: 'Search' })}
                </Button>
            </Stack>

            {loading && (
                <Box display="flex" justifyContent="center" padding={2}>
                    <CircularProgress size="small" />
                </Box>
            )}

            {!loading && error && (
                <Typography variant="body2" color="error">
                    {error}
                </Typography>
            )}

            {!loading && result && (
                <Stack gap={1}>
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2">
                            <code>{result.key}</code>
                        </Typography>
                        <Chip
                            size="small"
                            color={result.pricedInPriceBook ? 'success' : 'warning'}
                            label={
                                result.pricedInPriceBook
                                    ? formatMessage(
                                          {
                                              id: 'pricingTrace.keyEmitters.pricedForModels',
                                              defaultMessage: 'Priced for: {models}',
                                          },
                                          { models: result.pricedForModels.join(', ') },
                                      )
                                    : formatMessage({
                                          id: 'cpq.key-emitters.unpriced',
                                          defaultMessage: 'no price record',
                                      })
                            }
                        />
                    </Stack>

                    <Typography variant="caption" color="textSecondary">
                        {formatMessage(
                            {
                                id: 'cpq.key-emitters.exact-heading',
                                defaultMessage: 'Exact matches (decision-table rules): {count}',
                            },
                            { count: result.exactMatches.length },
                        )}
                    </Typography>
                    {result.exactMatches.map(match => (
                        <MatchRow key={`${match.procedureCode}-${match.ruleIndex}`} match={match} />
                    ))}

                    <Typography variant="caption" color="textSecondary">
                        {formatMessage(
                            {
                                id: 'cpq.key-emitters.candidate-heading',
                                defaultMessage:
                                    'Template candidates (emit rules, low confidence): {count}',
                            },
                            { count: result.templateCandidates.length },
                        )}
                    </Typography>
                    {result.templateCandidates.map(match => (
                        <MatchRow key={`${match.procedureCode}-${match.ruleIndex}`} match={match} />
                    ))}

                    {result.exactMatches.length === 0 && result.templateCandidates.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                            {formatMessage({
                                id: 'cpq.key-emitters.none',
                                defaultMessage: 'No rule found that emits this key.',
                            })}
                        </Typography>
                    )}
                </Stack>
            )}
        </Stack>
    );
}
